import React, { useState } from 'react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { OTPVerification } from './OTPVerification';
import { sendOTP, verifyOTP, sendOrderConfirmation, sendShopNotification } from '../utils/api';
import { saveOrder } from '../utils/supabase';
import { User, CateringOrder } from '../types';
import { CheckCircle, Calendar, Users, Utensils } from 'lucide-react';
export function CateringOrderForm() {
  const [step, setStep] = useState<'contact' | 'otp' | 'details' | 'success'>('contact');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Form State
  const [user, setUser] = useState<User>({
    name: '',
    email: '',
    phone: ''
  });
  const [orderDetails, setOrderDetails] = useState({
    eventDate: '',
    guestCount: 20,
    specialRequests: '',
    menuItems: [] as string[]
  });
  const menuOptions = ['Pulled Pork', 'Beef Brisket', 'Ribs', 'Smoked Chicken', 'Mac & Cheese', 'Coleslaw', 'Baked Beans', 'Potato Salad'];
  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await sendOTP(user.email);
      setStep('otp');
    } catch (err) {
      setError('Failed to send verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  const handleVerifyOTP = async (otp: string) => {
    setLoading(true);
    setError(null);
    try {
      const isValid = await verifyOTP(user.email, otp);
      if (isValid) {
        setStep('details');
      } else {
        setError('Invalid code. Please try again.');
      }
    } catch (err) {
      setError('Verification failed.');
    } finally {
      setLoading(false);
    }
  };
  const handleOrderSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const order: CateringOrder = {
      customer: user,
      ...orderDetails,
      status: 'pending',
      createdAt: new Date().toISOString()
    };
    try {
      // 1. Save to Supabase
      const {
        error: dbError
      } = await saveOrder(order);
      if (dbError) throw new Error('Database error');
      // 2. Send Emails
      await Promise.all([sendOrderConfirmation(user.email, order), sendShopNotification(order)]);
      setStep('success');
    } catch (err) {
      setError('Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  const toggleMenuItem = (item: string) => {
    setOrderDetails(prev => ({
      ...prev,
      menuItems: prev.menuItems.includes(item) ? prev.menuItems.filter(i => i !== item) : [...prev.menuItems, item]
    }));
  };
  return <div className="bg-dark-card p-6 md:p-8 rounded-xl border border-gray-800 shadow-2xl max-w-2xl mx-auto" data-id="element-13">
      {/* Progress Steps */}
      <div className="flex justify-between mb-8 relative" data-id="element-14">
        <div className="absolute top-1/2 left-0 w-full h-0.5 bg-gray-800 -z-10" data-id="element-15"></div>
        {['Contact', 'Verify', 'Details', 'Done'].map((label, idx) => {
        const currentIdx = ['contact', 'otp', 'details', 'success'].indexOf(step);
        const isActive = idx <= currentIdx;
        return <div key={label} className="flex flex-col items-center bg-dark-card px-2" data-id="element-16">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${isActive ? 'bg-primary text-white' : 'bg-gray-800 text-gray-500'}`} data-id="element-17">
                {idx + 1}
              </div>
              <span className={`text-xs mt-2 ${isActive ? 'text-primary' : 'text-gray-500'}`} data-id="element-18">
                {label}
              </span>
            </div>;
      })}
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/50 text-red-500 p-3 rounded-lg mb-6 text-sm" data-id="element-19">
          {error}
        </div>}

      {step === 'contact' && <form onSubmit={handleContactSubmit} className="space-y-4" data-id="element-20">
          <h2 className="text-2xl font-retro text-white mb-4" data-id="element-21">
            Start Your Order
          </h2>
          <Input label="Full Name" required value={user.name} onChange={e => setUser({
        ...user,
        name: e.target.value
      })} placeholder="Jane Doe" data-id="element-22" />
          <Input label="Email Address" type="email" required value={user.email} onChange={e => setUser({
        ...user,
        email: e.target.value
      })} placeholder="jane@example.com" data-id="element-23" />
          <Input label="Phone Number" type="tel" required value={user.phone} onChange={e => setUser({
        ...user,
        phone: e.target.value
      })} placeholder="(812) 555-0123" data-id="element-24" />
          <Button type="submit" className="w-full" isLoading={loading} data-id="element-25">
            Send Verification Code
          </Button>
        </form>}

      {step === 'otp' && <OTPVerification email={user.email} onVerify={handleVerifyOTP} onResend={() => sendOTP(user.email)} isVerifying={loading} data-id="element-26" />}

      {step === 'details' && <form onSubmit={handleOrderSubmit} className="space-y-6" data-id="element-27">
          <h2 className="text-2xl font-retro text-white mb-4" data-id="element-28">Event Details</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-29">
            <Input label="Event Date" type="date" required value={orderDetails.eventDate} onChange={e => setOrderDetails({
          ...orderDetails,
          eventDate: e.target.value
        })} data-id="element-30" />
            <Input label="Guest Count" type="number" min="10" required value={orderDetails.guestCount} onChange={e => setOrderDetails({
          ...orderDetails,
          guestCount: parseInt(e.target.value)
        })} data-id="element-31" />
          </div>

          <div data-id="element-32">
            <label className="block text-sm font-medium text-gray-300 mb-3" data-id="element-33">
              Menu Preferences
            </label>
            <div className="grid grid-cols-2 gap-3" data-id="element-34">
              {menuOptions.map(item => <label key={item} className="flex items-center space-x-3 p-3 rounded-lg border border-gray-700 hover:bg-gray-800 cursor-pointer transition-colors" data-id="element-35">
                  <input type="checkbox" checked={orderDetails.menuItems.includes(item)} onChange={() => toggleMenuItem(item)} className="w-4 h-4 text-primary rounded border-gray-600 focus:ring-primary bg-gray-700" data-id="element-36" />
                  <span className="text-sm text-gray-200" data-id="element-37">{item}</span>
                </label>)}
            </div>
          </div>

          <div data-id="element-38">
            <label className="block text-sm font-medium text-gray-300 mb-1" data-id="element-39">
              Special Requests
            </label>
            <textarea className="w-full px-3 py-2 bg-dark-lighter border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-white h-24" value={orderDetails.specialRequests} onChange={e => setOrderDetails({
          ...orderDetails,
          specialRequests: e.target.value
        })} placeholder="Allergies, dietary restrictions, or specific setup needs..." data-id="element-40" />
          </div>

          <Button type="submit" className="w-full" size="lg" isLoading={loading} data-id="element-41">
            Submit Catering Request
          </Button>
        </form>}

      {step === 'success' && <div className="text-center py-8" data-id="element-42">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-43">
            <CheckCircle className="w-10 h-10 text-green-500" data-id="element-44" />
          </div>
          <h2 className="text-3xl font-retro text-white mb-4" data-id="element-45">
            Order Received!
          </h2>
          <p className="text-gray-400 mb-8 max-w-md mx-auto" data-id="element-46">
            Thanks {user.name}! We've sent a confirmation to{' '}
            <strong data-id="element-47">{user.email}</strong>. We'll review your request and contact
            you shortly to finalize the details.
          </p>
          <Button onClick={() => window.location.href = '/'} variant="outline" data-id="element-48">
            Return Home
          </Button>
        </div>}
    </div>;
}